import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loading, StatusBadge, Ring, Avatar, timeAgo, fmtDateTime } from "@/components/common";
import AdvancedFilters from "@/components/AdvancedFilters";
import QuickFilters, { durationFilters } from "@/components/QuickFilters";

const lastResult = (p) => p.last_outcome || p.last_disposition || "";
const QF = [{ key: "last_result", label: "Last Call Results", get: (p) => lastResult(p) || "Not called" }, { key: "status", label: "Statuses" }, { key: "lead_stage", label: "Stages" }, { key: "assigned_name", label: "Assignees" }, { key: "city", label: "Cities" }, { key: "course_interest", label: "Courses" }, { key: "exam", label: "Exams" }, { key: "tag", label: "Tags" }, { key: "attempts", label: "Attempts", get: (p) => (p.attempts >= 5 ? "5+" : String(p.attempts || 0)) }, ...durationFilters];
import { OutcomePanel, PField, ProspectDialog, ConvertDialog, ActivityList } from "@/components/ProspectTools";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { ChevronLeft, Phone, ListChecks, MessageSquare, MessageCircle, StickyNote, Sparkles, PhoneForwarded, Pencil, CalendarPlus, UserCog } from "lucide-react";
import { toast } from "sonner";
import { callViaPhone } from "@/lib/phone";

const PIE = ["#4F46E5", "#818CF8", "#22C55E", "#06B6D4", "#F59E0B", "#DB2777", "#0EA5E9", "#A855F7"];
const PROSPECT_STATUSES = ["New", "Assigned", "Calling", "Contacted", "Interested", "Callback", "No Answer", "Not Interested", "Invalid Number", "Wrong Number", "Already Admitted", "Not Eligible", "Do Not Call", "Converted to Lead"];

export const TONES = ["bg-emerald-100 text-emerald-700", "bg-sky-100 text-sky-700", "bg-amber-100 text-amber-700", "bg-green-100 text-green-700", "bg-violet-100 text-violet-700", "bg-rose-100 text-rose-700", "bg-indigo-100 text-indigo-700"];

export function FieldCard({ title, tone, children, onEdit, testid }) {
  return (
    <div className={`rounded-xl border-l-4 p-3 shadow-sm ${tone}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-2 flex items-center justify-between">{title}
        {onEdit && <button type="button" onClick={onEdit} className="normal-case tracking-normal text-[11px] font-semibold underline-offset-2 hover:underline" data-testid={testid}>✎ Edit</button>}
      </div>
      <div className="grid grid-cols-2 gap-y-2.5 gap-x-2 text-slate-800">{children}</div>
    </div>
  );
}

export default function CampaignDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [camp, setCamp] = useState(null);
  const [prospects, setProspects] = useState(null);
  const [display, setDisplay] = useState(null);
  const [quick, setQuick] = useState(null);
  const [selId, setSelId] = useState(null);
  useEffect(() => { if (display && display.length && !display.some((p) => p.id === selId)) setSelId(display[0].id); }, [display]); // eslint-disable-line
  const [leadByMobile, setLeadByMobile] = useState({});
  const [modal, setModal] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [timeline, setTimeline] = useState({ calls: [], activities: [], notes: [] });

  const loadProspects = async (c) => {
    const { data } = await api.get(`/prospects?campaign=${encodeURIComponent(c.name)}&all_rows=1`);
    setProspects(data.map((p) => ({ ...p, lead_stage: "" })));
    setSelId((cur) => cur || data[0]?.id || null);
  };

  useEffect(() => {
    api.get("/calling-campaigns").then(async ({ data }) => {
      const c = data.find((x) => x.id === id) || data[0];
      setCamp(c);
      if (!c) { setProspects([]); return; }
      await loadProspects(c);
      try {
        const lr = await api.get(`/leads-by-campaign?campaign=${encodeURIComponent(c.name)}`);
        const map = {}; lr.data.forEach((l) => { if (l.mobile) map[l.mobile] = l; });
        setLeadByMobile(map);
      } catch { /* ignore */ }
    });
  }, [id]); // eslint-disable-line

  const rows = useMemo(() => (prospects === null ? null : prospects.map((p) => ({ ...p, lead_stage: leadByMobile[p.mobile]?.stage || "" }))), [prospects, leadByMobile]);
  const sel = (rows || []).find((p) => p.id === selId) || null;
  const refreshTimeline = () => { if (sel) api.get(`/prospects/${sel.id}/timeline`).then(({ data }) => setTimeline(data)).catch(() => {}); };
  useEffect(() => { refreshTimeline(); }, [selId]); // eslint-disable-line

  const FIELDS = useMemo(() => [
    { key: "name", label: "Name", type: "text" }, { key: "mobile", label: "Mobile", type: "text" },
    { key: "status", label: "Status", type: "select", options: PROSPECT_STATUSES },
    { key: "last_disposition", label: "Last Call Result", type: "text" }, { key: "last_call_duration", label: "Last Call Duration (sec)", type: "number" },
    { key: "talk_seconds", label: "Total Talk Time (sec)", type: "number" },
    { key: "lead_stage", label: "Lead Stage", type: "text" }, { key: "assigned_name", label: "Assignee", type: "text" },
    { key: "city", label: "City", type: "text" }, { key: "state", label: "State", type: "text" },
    { key: "course_interest", label: "Interested Course", type: "text" }, { key: "exam", label: "Exam", type: "text" },
    { key: "rank", label: "Rank", type: "number" }, { key: "attempts", label: "Attempts", type: "number" },
    { key: "last_outcome", label: "Last Outcome", type: "text" }, { key: "source", label: "Source", type: "text" },
    { key: "last_call_at", label: "Last Call", type: "date" }, { key: "created_at", label: "Created On", type: "date" },
  ], []);

  if (!camp || prospects === null) return <Loading label="Loading campaign" />;
  const contacted = prospects.filter((p) => ["Contacted", "Interested", "Converted to Lead"].includes(p.status)).length;
  const pct = prospects.length ? Math.round((contacted / prospects.length) * 100) : 0;
  const dist = {};
  prospects.forEach((p) => { const k = p.assigned_name || "Unassigned"; dist[k] = (dist[k] || 0) + 1; });
  const pie = Object.entries(dist).map(([name, value]) => ({ name, value }));
  const patchSel = (d) => { if (d) setProspects((ps) => ps.map((p) => (p.id === d.id ? { ...p, ...d } : p))); refreshTimeline(); };
  const list = display || [];

  const ACTIONS = [["call", Phone, "Call"], ["task", ListChecks, "Task"], ["fu", CalendarPlus, "Follow-up"], ["wa", MessageSquare, "WA"], ["sms", MessageCircle, "SMS"], ["note", StickyNote, "Note"], ["assign", UserCog, "Assignee"]];
  const runAction = (m) => {
    if (m === "call") { callViaPhone({ phone: sel.mobile, name: sel.name, entity_type: "prospect", entity_id: sel.id }); setCallOpen(true); return; }
    setModal(m);
  };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8 min-h-[calc(100vh-4rem)]">
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_340px] bg-slate-50 lg:h-[calc(100vh-4rem)]">
        <div className="border-r border-slate-200 overflow-y-auto p-4 bg-white">
          <button onClick={() => navigate("/calling-campaigns")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#312E81] mb-3" data-testid="camp-back-btn"><ChevronLeft className="w-4 h-4" /> Campaigns</button>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Campaign Dashboard</div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mt-2">
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-[#312E81] text-sm" data-testid="camp-name">{camp.name}</div>
              <Ring value={pct} size={48} />
            </div>
            <div className="flex items-center gap-3 mt-3 text-xs text-slate-500"><span>{prospects.length} leads</span><span>{camp.team} team</span></div>
            <Button className="w-full mt-3 bg-[#4F46E5] hover:bg-[#4338CA]" onClick={() => { sessionStorage.setItem("ohc_queue", JSON.stringify({ campaign: camp?.name, ids: (display || quick || rows || []).map((r) => r.id) })); navigate("/calling-queue"); }} data-testid="camp-start-calling"><PhoneForwarded className="w-4 h-4 mr-1" /> Start Calling</Button>
          </div>
          <div className="mt-4">
            <div className="font-heading font-semibold text-[#312E81] text-sm mb-2">Campaign Assignees Report</div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Tooltip /><Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}>{pie.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}</Pie></PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {pie.map((p, i) => (
                <div key={p.name} className="flex items-center gap-2 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} /><span className="text-slate-600 flex-1 truncate">{p.name}</span><span className="text-slate-400">{Math.round((p.value / prospects.length) * 100)}%</span></div>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-y-auto">
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 pt-2.5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading font-bold text-[#312E81] text-sm">{camp.name}</span>
              <span className="text-xs font-semibold text-[#4F46E5]" data-testid="camp-lead-count">Showing {list.length} of {prospects.length}</span>
            </div>
            <QuickFilters rows={rows} fields={QF} onFiltered={setQuick} testid="camp-leads" />
            <AdvancedFilters entity="campaign_leads" fields={FIELDS} rows={quick} onFiltered={setDisplay} />
          </div>
          <div className="p-3 space-y-2">
            {list.map((p, i) => (
              <button key={p.id} onClick={() => setSelId(p.id)} data-testid={`camp-lead-${i}`}
                className={`w-full text-left bg-white border rounded-xl p-3 shadow-sm hover:border-[#4F46E5]/40 ${selId === p.id ? "border-[#4F46E5]" : "border-slate-200"}`}>
                <div className="font-heading font-bold text-[#4F46E5] flex items-center gap-2">{p.name}{p.edited_fields && Object.keys(p.edited_fields).length > 0 && <span className="px-1 rounded bg-amber-100 text-amber-700 text-[9px] font-semibold">Edited</span>}</div>
                <div className="text-xs text-slate-500 font-mono">{p.mobile}</div>
                <div className="flex items-center gap-2 mt-1 text-xs flex-wrap"><span className="text-slate-500">Last call:</span>{lastResult(p) ? <StatusBadge value={lastResult(p)} /> : <span className="text-slate-400" data-testid={`camp-lead-result-${i}`}>Not called</span>}{p.last_call_duration > 0 && <span className="text-slate-400">{Math.floor(p.last_call_duration / 60)}m {p.last_call_duration % 60}s</span>}<span className="text-slate-500 ml-1">Status:</span><StatusBadge value={p.status} />{p.lead_stage && <><span className="text-slate-500 ml-1">Stage:</span><StatusBadge value={p.lead_stage} /></>}</div>
                <div className="text-[11px] text-slate-400 mt-1">{p.last_call_at ? `Last call ${timeAgo(p.last_call_at)}` : "Not called yet"}{p.assigned_name ? ` · ${p.assigned_name}` : ""}</div>
              </button>
            ))}
            {list.length === 0 && <div className="text-center text-sm text-slate-400 py-10" data-testid="camp-no-leads">No prospects match</div>}
          </div>
        </div>

        <aside className="flex flex-col border-l-4 border-[#6366F1] bg-gradient-to-b from-indigo-50 via-white to-amber-50/40 overflow-y-auto" data-testid="camp-lead-detail">
          {sel && (
            <>
              <div className="p-4 bg-gradient-to-br from-[#312E81] via-[#4338CA] to-[#6366F1] text-white">
                <div className="flex items-center gap-2"><div className="ring-2 ring-white/60 rounded-full"><Avatar name={sel.name} size={40} /></div><div className="flex-1 min-w-0"><div className="font-heading font-bold truncate">{sel.name}</div><div className="text-[11px] text-indigo-100">{sel.mobile} · {sel.city || "—"}</div><span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-amber-300 text-[#312E81] text-[10px] font-bold" data-testid="camp-sel-status">{sel.status || "New"}</span></div>
                  <button onClick={() => setModal("edit")} className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" title="Edit lead fields" data-testid="camp-edit-btn"><Pencil className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1.5 p-3 border-b border-indigo-100">
                {ACTIONS.map(([m, Icon, l], i) => (
                  <button key={m} onClick={() => runAction(m)} data-testid={`camp-action-${m}`} className={`flex flex-col items-center gap-1 py-2 rounded-lg transition-transform hover:-translate-y-0.5 ${TONES[i % TONES.length]}`}><Icon className="w-4 h-4" /><span className="text-[10px] font-semibold">{l}</span></button>
                ))}
              </div>
              <div className="p-3 space-y-2 text-xs" data-testid="camp-lead-fields">
                <FieldCard title="Contact" tone="border-sky-400 bg-sky-50/70 text-sky-700">
                  <PField k="Name" field="name" p={sel} /><PField k="Phone" field="mobile" p={sel} />
                  <PField k="Alt Phone" field="alt_mobile" p={sel} /><PField k="Email" field="email" p={sel} />
                  <PField k="City" field="city" p={sel} /><PField k="State" field="state" p={sel} />
                </FieldCard>
                <FieldCard title="Academic" tone="border-emerald-400 bg-emerald-50/70 text-emerald-700">
                  <PField k="Exam" field="exam" p={sel} /><PField k="Rank" field="rank" p={sel} />
                  <PField k="Interest" field="course_interest" p={sel} /><PField k="Language" field="language" p={sel} />
                </FieldCard>
                <FieldCard title="Pipeline" tone="border-amber-400 bg-amber-50/80 text-amber-700">
                  <PField k="Assignee" field="assigned_name" p={sel} /><PField k="Last Call Result" v={lastResult(sel) || "Not called"} /><PField k="Status" v={sel.status} />
                  <PField k="Lead Stage" v={sel.lead_stage} /><PField k="Attempts" v={sel.attempts} />
                  <PField k="Next Action" v={sel.next_action_at ? fmtDateTime(sel.next_action_at) : "—"} /><PField k="Last Outcome" v={sel.last_outcome} />
                </FieldCard>
              </div>
              <div className="p-3">
                <div className="rounded-xl border-l-4 border-violet-400 bg-violet-50/70 p-3">
                  <div className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Activity History</div>
                  <div className="space-y-2 h-80 overflow-y-scroll pr-1" data-testid="camp-activity"><ActivityList timeline={timeline} /></div>
                </div>
              </div>
            </>
          )}
        </aside>
      </div>

      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Call Outcome · {sel?.name}</DialogTitle></DialogHeader>
          {sel && <OutcomePanel prospect={sel} calling onSaved={(d) => { patchSel(d); setCallOpen(false); }} onInterested={(d) => { patchSel(d); setCallOpen(false); setConvertOpen(true); }} />}
        </DialogContent>
      </Dialog>
      <ConvertDialog prospect={sel} open={convertOpen} onOpenChange={setConvertOpen} onDone={() => loadProspects(camp)} />
      <ProspectDialog mode={modal} prospect={sel} onClose={() => setModal(null)} onSaved={patchSel} />
    </div>
  );
}
